/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useRef, useState } from "react";
import { Marker, Popup, useMap } from "react-leaflet";
import { Icon, polyline } from "leaflet";
import { popupcontext } from "../../../context/Popupscontext";
import { useLazyGetSpecificDeviceQuery } from "../../../Redux/service/Devices";
import PositionsPopups from "../../Positions/Positions-Popups/PositionsPopups";
import "leaflet-rotatedmarker";

export default function PlayBackPositions({
	HistoryPlayBack,
	setColors,
	mainhistorypositions,
	playBackSpeed,
	settings,
}) {
	const { isPlaying, setIsPlaying, posIndex, setPosIndex } =
		useContext(popupcontext);
	const [getStatus] = useLazyGetSpecificDeviceQuery();
	const [deviceStatus, setDeviceStatus] = useState({});
	const [colors, setColorsState] = useState([]);
	const currentMarkerRef = useRef(null);
	const map = useMap();
	const polylinesRef = useRef([]); // Reference to store polylines for removal

	// Generate a random color
	function generateRandomColor() {
		let r, g, b;
		do {
			r = Math.floor(Math.random() * 256);
			g = Math.floor(Math.random() * 256);
			b = Math.floor(Math.random() * 256);
		} while (g > 100);

		if (r === 255 && g === 255 && b === 255) {
			return generateRandomColor();
		}

		return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
	}

	// Generate colors only when mainhistorypositions changes
	useEffect(() => {
		if (mainhistorypositions.length === 0) return;
		map.setZoom(12);
		// Avoid unnecessary re-renders
		if (colors.length === mainhistorypositions.length) return;

		const newColors = HistoryPlayBack.map((i) => {
			if (i.type === "Moving") {
				const randomColor = generateRandomColor();
				return Array.from({ length: i.data.length }, () => randomColor);
			} else if (i.type === "Idle") {
				return Array.from({ length: i.data.length }, () => "#F59E0B");
			} else {
				return Array.from({ length: i.data.length }, () => "#1E88E5");
			}
		});
		setColorsState(newColors);
		setColors(newColors); // Update parent component state
	}, [mainhistorypositions, map, setColors]);

	// Apply polyline colors
	useEffect(() => {
		setPolylineColors(mainhistorypositions, colors.flat());
	}, [mainhistorypositions, colors]);

	// Function to set polyline colors and remove old ones
	function setPolylineColors(mainhistorypositions, colors) {
		// Remove old polylines
		polylinesRef.current.forEach((poly) => poly.remove());
		polylinesRef.current = []; // Reset polyline reference

		// Create new polylines
		if (mainhistorypositions.length > 1 && colors.length > 0) {
			for (let i = 0; i < mainhistorypositions.length - 1; i++) {
				const newPolyline = polyline(
					[
						[
							mainhistorypositions[i].latitude,
							mainhistorypositions[i].longitude,
						],
						[
							mainhistorypositions[i + 1].latitude,
							mainhistorypositions[i + 1].longitude,
						],
					],
					{ color: colors[i] }
				).addTo(map);

				// Store reference to the new polyline
				polylinesRef.current.push(newPolyline);
			}
		}
	}

	// Auto-playback logic
	useEffect(() => {
		if (!isPlaying || mainhistorypositions.length === 0) {
			return;
		}
		const interval = setInterval(() => {
			setPosIndex((prevIndex) => {
				if (prevIndex < mainhistorypositions.length - 1) {
					return prevIndex + 1;
				}
				setIsPlaying(false);
				return prevIndex;
			});
		}, Number(playBackSpeed) * 1000);

		return () => clearInterval(interval);
	}, [
		isPlaying,
		mainhistorypositions,
		setIsPlaying,
		setPosIndex,
		playBackSpeed,
	]);

	// Auto-center map on marker
	useEffect(() => {
		if (mainhistorypositions.length === 0 || posIndex === undefined) return;
		const markerPos = mainhistorypositions[posIndex];
		if (!markerPos) return;
		const markerLatLon = [markerPos.latitude, markerPos.longitude];
		const bounds = map.getBounds();
		if (!bounds.contains(markerLatLon)) {
			map.panTo(markerLatLon, { animate: true, duration: playBackSpeed });
		}
	}, [posIndex, mainhistorypositions, map, playBackSpeed]);

	// Rotate marker based on course
	useEffect(() => {
		if (
			currentMarkerRef.current &&
			mainhistorypositions[posIndex]?.course !== undefined
		) {
			currentMarkerRef.current.setRotationAngle(
				mainhistorypositions[posIndex].course
			);
		}
	}, [posIndex, mainhistorypositions]);

	const handleMarkerClick = async (pos) => {
		try {
			const { data: device } = await getStatus(pos.deviceId);
			if (device) {
				setDeviceStatus((prevStatus) => ({
					...prevStatus,
					[pos.deviceId]: device.status !== "offline",
				}));
			}
		} catch (error) {
			console.error("Error fetching device status:", error);
		}
	};

	if (!mainhistorypositions || mainhistorypositions.length === 0) {
		return null;
	}

	const icons = {
		first: new Icon({ iconUrl: "/StartPoint.svg", iconSize: [20, 20] }),
		last: new Icon({ iconUrl: "/EndPoint.svg", iconSize: [20, 20] }),
		default: new Icon({ iconUrl: "/Positions.svg", iconSize: [24, 24] }),
		current: new Icon({ iconUrl: "/assets/CarOnline.svg", iconSize: [40, 40] }),
		parking: new Icon({
			iconUrl: "assets/ParkingStatus.svg",
			iconSize: [40, 40],
		}),
		idle: new Icon({ iconUrl: "assets/IdleStatus.svg", iconSize: [40, 40] }),
	};

	const statusIcon = (type) => {
		if (type === "Parking" && settings?.parkingPeriods) return icons.parking;
		if (type === "Idle" && settings?.idlePeriods) return icons.idle;
		return null;
	};
	console.log("History Play", mainhistorypositions);
	return (
		<div>
			{settings?.mapPoints &&
				mainhistorypositions.map((pos, index) => (
					<Marker
						key={index}
						icon={
							index === 0
								? icons.first
								: index === mainhistorypositions.length - 1
								? icons.last
								: icons.default
						}
						position={[pos.latitude, pos.longitude]}
						eventHandlers={{
							click: () => {
								handleMarkerClick(pos);
							},
						}}
					>
						<Popup closeButton={false}>
							<PositionsPopups
								mark={pos}
								lat={pos.latitude}
								lon={pos.longitude}
								devicestatus={deviceStatus}
							/>
						</Popup>
					</Marker>
				))}

			{mainhistorypositions[posIndex] && (
				<Marker
					ref={currentMarkerRef}
					icon={icons.current}
					position={[
						mainhistorypositions[posIndex].latitude,
						mainhistorypositions[posIndex].longitude,
					]}
					rotationAngle={mainhistorypositions[posIndex].course || 0}
					rotationOrigin="center"
				>
					<Popup>
						<PositionsPopups
							mark={mainhistorypositions[posIndex]}
							lat={mainhistorypositions[posIndex].latitude}
							lon={mainhistorypositions[posIndex].longitude}
							devicestatus={deviceStatus}
						/>
					</Popup>
				</Marker>
			)}

			{HistoryPlayBack.map((i, index) => {
				const icon = statusIcon(i.type);

				if (!icon) {
					return null; // skip rendering this marker
				} else {
					return (
						<Marker
							key={index}
							icon={icon}
							position={[
								i.data[0].latitude + 0.00001,
								i.data[0].longitude + 0.00001,
							]}
						/>
					);
				}
			})}
		</div>
	);
}
